import queryString from "query-string";
import { api, authHeader } from "../configs/axiosConfigs";

const BASE = "api/admin/partner-commission";

const pickField = (obj, ...keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

/** ApiResult from endpoints wrapped with Ok() may nest data twice — unwrap to the list. */
const unwrapApiPayload = (payload, depth = 0) => {
  if (payload == null || depth > 5) return payload;
  if (Array.isArray(payload)) return payload;

  const inner = payload.data ?? payload.Data;
  if (inner == null) return payload;
  if (Array.isArray(inner)) return inner;

  if (
    typeof inner === "object" &&
    (inner.data != null ||
      inner.Data != null ||
      inner.isSuccess != null ||
      inner.IsSuccess != null)
  ) {
    return unwrapApiPayload(inner, depth + 1);
  }

  return inner;
};

const partnerLevelMap = {
  Level1: 1,
  Level2: 2,
};

const partnerTypeMap = {
  PublicReferrer: 1,
  ProfessionalPartner: 2,
  TelecomPartner: 3,
  LoanPartner: 4,
  SettlementPartner: 5,
};

const partnerStatusMap = {
  PendingActivation: 1,
  Active: 2,
  Suspended: 3,
  Inactive: 4,
};

const reverseMap = (map) =>
  Object.entries(map).reduce((acc, [key, value]) => {
    acc[value] = key;
    acc[String(value)] = key;
    return acc;
  }, {});

const partnerLevelNameMap = reverseMap(partnerLevelMap);
const partnerTypeNameMap = reverseMap(partnerTypeMap);
const partnerStatusNameMap = reverseMap(partnerStatusMap);

const normalizeEnumValue = (value, map) => {
  if (value == null || value === "") return value;
  if (typeof value === "number") return value;
  if (map[value] != null) return map[value];
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : value;
};

const normalizeEnumName = (value, reverseMap) => {
  if (value == null || value === "") return value;
  if (typeof value === "string") {
    if (reverseMap[value] != null) return value;
    const numeric = Number(value);
    if (Number.isFinite(numeric) && reverseMap[numeric] != null) return reverseMap[numeric];
    return value;
  }
  if (typeof value === "number") {
    return reverseMap[value] != null ? reverseMap[value] : String(value);
  }
  return value;
};

const normalizePartnerPayload = (payload) => {
  if (!payload || typeof payload !== "object") return payload;
  return {
    ...payload,
    partnerLevel: normalizeEnumValue(payload.partnerLevel, partnerLevelMap),
    partnerType: normalizeEnumValue(payload.partnerType, partnerTypeMap),
    status: normalizeEnumValue(payload.status, partnerStatusMap),
  };
};

export const normalizePartnerProfile = (row) => {
  if (!row || typeof row !== "object") return row;

  return {
    ...row,
    id: pickField(row, "id", "Id"),
    userId: pickField(row, "userId", "UserId"),
    displayName: pickField(row, "displayName", "DisplayName"),
    mobileNumber: pickField(row, "mobileNumber", "MobileNumber"),
    parentPartnerId: pickField(row, "parentPartnerId", "ParentPartnerId"),
    partnerLevel: normalizeEnumName(
      pickField(row, "partnerLevel", "PartnerLevel", "level", "Level"),
      partnerLevelNameMap
    ),
    partnerType: normalizeEnumName(
      pickField(row, "partnerType", "PartnerType"),
      partnerTypeNameMap
    ),
    status: normalizeEnumName(
      pickField(row, "status", "Status"),
      partnerStatusNameMap
    ),
    commissionGroupId: pickField(row, "commissionGroupId", "CommissionGroupId"),
    referralCode: pickField(row, "referralCode", "ReferralCode"),
    canCreateSubPartners: pickField(
      row,
      "canCreateSubPartners",
      "CanCreateSubPartners"
    ),
    joinedAt: pickField(row, "joinedAt", "JoinedAt"),
    leftAt: pickField(row, "leftAt", "LeftAt"),
    teamMemberCount: pickField(row, "teamMemberCount", "TeamMemberCount") ?? 0,
    activeTeamMemberCount:
      pickField(row, "activeTeamMemberCount", "ActiveTeamMemberCount") ?? 0,
    teamTotalCommissionGenerated:
      pickField(row, "teamTotalCommissionGenerated", "TeamTotalCommissionGenerated") ??
      0,
    teamTotalPaidCommission:
      pickField(row, "teamTotalPaidCommission", "TeamTotalPaidCommission") ?? 0,
  };
};

const isPartnerActiveStatus = (status) =>
  status === "Active" || status === 2 || status === "2";

const mapPartnerEnumsToNames = (row) => {
  if (!row || typeof row !== "object") return row;
  return {
    ...row,
    partnerLevel: normalizeEnumName(row.partnerLevel, partnerLevelNameMap),
    partnerType: normalizeEnumName(row.partnerType, partnerTypeNameMap),
    status: normalizeEnumName(row.status, partnerStatusNameMap),
  };
};

const isLevel1Partner = (partner) => {
  const level = partner?.partnerLevel ?? partner?.PartnerLevel ?? partner?.level ?? partner?.Level;
  return level === "Level1" || level === 1 || level === "1";
};

const mapPartnerList = (list) =>
  (Array.isArray(list) ? list : []).map(normalizePartnerProfile);

const buildLevel1PartnersFallback = (allPartners) => {
  const partners = Array.isArray(allPartners) ? allPartners : [];
  const level1 = partners.filter(isLevel1Partner);

  return level1.map((parent) => {
    const children = partners.filter(
      (p) => (p.parentPartnerId ?? p.ParentPartnerId) === (parent.id ?? parent.Id)
    );
    return {
      ...parent,
      teamMemberCount: children.length,
      activeTeamMemberCount: children.filter((c) =>
        isPartnerActiveStatus(c.status ?? c.Status)
      ).length,
      teamTotalCommissionGenerated: 0,
      teamTotalPaidCommission: 0,
    };
  });
};

const buildTeamMembersFallback = (allPartners, parentPartnerId) =>
  (Array.isArray(allPartners) ? allPartners : [])
    .filter((p) => (p.parentPartnerId ?? p.ParentPartnerId) === parentPartnerId)
    .map((p) => ({
      partnerId: p.id ?? p.Id,
      userId: p.userId ?? p.UserId,
      displayName: p.displayName ?? p.DisplayName,
      mobileNumber: p.mobileNumber ?? p.MobileNumber,
      status: p.status ?? p.Status,
      partnerLevel: p.partnerLevel ?? p.PartnerLevel,
      referralCode: p.referralCode ?? p.ReferralCode,
      currentSharePercent: null,
      createdAt: p.joinedAt ?? p.JoinedAt,
      totalCommissionGenerated: 0,
      totalPaidCommission: 0,
    }));

const requestRaw = async (url, method = "GET", data) => {
  const response = await api.request({
    url,
    method,
    data,
    headers: { ...authHeader(), "Content-Type": "application/json" },
    validateStatus: () => true,
  });
  return response;
};

const request = async (url, method = "GET", data) => {
  try {
    const response = await api.request({
      url,
      method,
      data,
      headers: { ...authHeader(), "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error) {
    const res = error?.response?.data;
    if (res) return res;
    throw error;
  }
};

const normalize = (res, fallbackMessage) => {
  const isSuccess = res?.isSuccess ?? res?.IsSuccess;
  if (!res || isSuccess === false) {
    return {
      error: res?.message || res?.Message || fallbackMessage,
      data: null,
      statusCode: res?.statusCode ?? res?.StatusCode,
    };
  }
  return {
    error: null,
    data: unwrapApiPayload(res.data ?? res.Data ?? res),
    statusCode: res?.statusCode ?? res?.StatusCode,
  };
};

const MarketGrowthAdminApi = {
  getGroups: () => request(`${BASE}/groups`),
  getGroupById: (id) => request(`${BASE}/groups/${id}`),
  createGroup: (payload) => request(`${BASE}/groups`, "POST", payload),
  updateGroup: (id, payload) => request(`${BASE}/groups/${id}`, "PUT", payload),
  patchGroupStatus: (id, isActive) =>
    request(`${BASE}/groups/${id}/status`, "PATCH", { isActive }),

  getRules: () => request(`${BASE}/rules`),
  getRulesByGroup: (groupId) => request(`${BASE}/groups/${groupId}/rules`),
  getRuleById: (id) => request(`${BASE}/rules/${id}`),
  createRule: (payload) => request(`${BASE}/rules`, "POST", payload),
  updateRule: (id, payload) => request(`${BASE}/rules/${id}`, "PUT", payload),
  patchRuleStatus: (id, isActive) =>
    request(`${BASE}/rules/${id}/status`, "PATCH", { isActive }),

  getPartners: () => request(`${BASE}/partners`),
  getPendingApprovalPartners: async () => {
    const res = await request(`${BASE}/partners`);
    const normalized = normalize(res, "بارگذاری نمایندگان در انتظار تایید ناموفق بود.");
    if (normalized.error) return res;

    const isSuccess = res?.isSuccess ?? res?.IsSuccess;
    const list = mapPartnerList(
      (Array.isArray(normalized.data) ? normalized.data : []).filter(
        (p) => String(p.status || "").toLowerCase() === "pendingactivation"
      )
    );
    return {
      ...(res || {}),
      isSuccess: isSuccess !== false,
      IsSuccess: isSuccess !== false,
      data: list,
      Data: list,
    };
  },
  getLevel1Partners: async () => {
    const candidates = [
      `${BASE}/partners/level1`,
      `${BASE}/partners?level=Level1&includeTeamSummary=true`,
    ];

    for (const url of candidates) {
      const response = await requestRaw(url);
      if (response.status === 404) continue;

      const normalized = normalize(response.data, "بارگذاری مدیران فروش ناموفق بود.");
      if (normalized.error) continue;

      const list = mapPartnerList(
        Array.isArray(normalized.data) ? normalized.data : []
      );
      return {
        ...(response.data || {}),
        isSuccess: true,
        IsSuccess: true,
        data: list,
        Data: list,
      };
    }

    const allRes = await request(`${BASE}/partners`);
    const normalized = normalize(allRes, "بارگذاری مدیران فروش ناموفق بود.");
    if (normalized.error) return allRes;

    const isSuccess = allRes?.isSuccess ?? allRes?.IsSuccess;
    const payload = mapPartnerList(buildLevel1PartnersFallback(normalized.data));
    return {
      ...(allRes || {}),
      isSuccess: isSuccess !== false,
      IsSuccess: isSuccess !== false,
      data: payload,
      Data: payload,
    };
  },
  getPartnerTeamMembers: async (parentPartnerId) => {
    const response = await requestRaw(`${BASE}/partners/${parentPartnerId}/team-members`);
    if (response.status !== 404) return response.data;

    const allRes = await request(`${BASE}/partners`);
    const normalized = normalize(allRes, "بارگذاری اعضای تیم ناموفق بود.");
    if (normalized.error) return allRes;

    const isSuccess = allRes?.isSuccess ?? allRes?.IsSuccess;
    const payload = buildTeamMembersFallback(normalized.data, parentPartnerId);
    return {
      ...(allRes || {}),
      isSuccess: isSuccess !== false,
      IsSuccess: isSuccess !== false,
      data: payload,
      Data: payload,
    };
  },
  getPartnerById: (id) => request(`${BASE}/partners/${id}`),
  createPartner: (payload) => request(`${BASE}/partners`, "POST", normalizePartnerPayload(payload)),
  updatePartner: (id, payload) => request(`${BASE}/partners/${id}`, "PUT", normalizePartnerPayload(payload)),
  patchPartnerStatus: (id, status) =>
    request(`${BASE}/partners/${id}/status`, "PATCH", {
      status: normalizeEnumValue(status, partnerStatusMap),
    }),
  patchPartnerCommissionGroup: (id, commissionGroupId) =>
    request(`${BASE}/partners/${id}/commission-group`, "PATCH", { commissionGroupId }),

  getTeamSharesByParent: (parentPartnerId) =>
    request(`${BASE}/partners/${parentPartnerId}/team-shares`),
  createTeamShare: (payload) => request(`${BASE}/team-shares`, "POST", payload),
  getTeamShareHistory: (params = {}) =>
    request(`${BASE}/team-shares/history?${queryString.stringify(params)}`),

  getCommissions: (params = {}) =>
    request(`${BASE}/commissions?${queryString.stringify(params)}`),
  getCommissionById: (id) => request(`${BASE}/commissions/${id}`),
  approveCommission: (id, notes) =>
    request(`${BASE}/commissions/${id}/approve-manual`, "POST", { notes: notes || null }),
  rejectCommission: (id, notes) =>
    request(`${BASE}/commissions/${id}/reject`, "POST", { notes: notes || null }),
  markCommissionPayable: (id, notes) =>
    request(`${BASE}/commissions/${id}/mark-payable`, "POST", { notes: notes || null }),
  markCommissionPaid: (id, notes) =>
    request(`${BASE}/commissions/${id}/mark-paid`, "POST", { notes: notes || null }),

  getPayouts: (params = {}) =>
    request(`${BASE}/payouts?${queryString.stringify(params)}`),
  getPayoutById: (id) => request(`${BASE}/payouts/${id}`),
  approvePayout: (id, notes) =>
    request(`${BASE}/payouts/${id}/approve`, "POST", { notes: notes || null }),
  rejectPayout: (id, notes) =>
    request(`${BASE}/payouts/${id}/reject`, "POST", { notes: notes || null }),
  markPayoutPaid: (id, notes) =>
    request(`${BASE}/payouts/${id}/mark-paid`, "POST", { notes: notes || null }),

  getAuditLogs: (params = {}) =>
    request(`${BASE}/audit-logs?${queryString.stringify(params)}`),
  getAttributions: (params = {}) =>
    request(`${BASE}/attributions?${queryString.stringify(params)}`),

  getDashboard: () => request(`${BASE}/dashboard`),

  getSettings: () => request(`${BASE}/settings`),
  updateSettings: (payload) => request(`${BASE}/settings`, "PUT", payload),

  getFraudAlerts: (params = {}) =>
    request(`${BASE}/fraud-alerts?${queryString.stringify(params)}`),

  exportCommissions: (params = {}) =>
    request(
      `${BASE}/export/commissions?${queryString.stringify({ exportType: 1, ...params })}`
    ),
  exportPayouts: (params = {}) =>
    request(
      `${BASE}/export/payouts?${queryString.stringify({ exportType: 1, ...params })}`
    ),
  exportAttributions: (params = {}) =>
    request(
      `${BASE}/export/attributions?${queryString.stringify({ exportType: 1, ...params })}`
    ),

  normalizePartnerProfile,
  normalizeList: (res, fallbackMessage) => {
    const result = normalize(res, fallbackMessage);
    if (result.error) return result;
    const list = Array.isArray(result.data) ? result.data : [];
    return { ...result, data: list };
  },
  normalize,
  mapPartnerEnumsToNames,
};

export default MarketGrowthAdminApi;
